import fetch from 'isomorphic-fetch';
import { retry } from '../utils/AsyncUtils';
import { btoa } from '../utils/EnvUtils';
import { ResponseError } from '../utils/ResponseError';

export type TwilioClient = {
    /**
     * @see https://www.twilio.com/docs/lookup/api
     *
     * @returns Sanitized and localized phone numbers or `null` if `phoneNumber` is not a valid phone number.
     */
    sanitizePhoneNumber: (phoneNumber: string) => Promise<null | {
        /** Phone number localized as per the number's country code. */
        localePhoneNumber: string;

        /** Phone number in E.164 format. */
        sanitizedPhoneNumber: string;
    }>;

    /**
     * Send a single SMS message.
     *
     * @throws On network error or error response.
     */
    sendSms: (opts: {
        /** OTP receiver phone number in E.164 format, e.g. `+16175551212`. */
        receiver: string;

        /** Message body, e.g. `Hello!\n1234`. */
        body: string;
    }) => Promise<void>;
};

export function createTwilioClient(opts: {
    accountSid: string;
    authToken: string;
    fromNumber: string;
    retryOpts?: { exponentialBackOff: number };
}): TwilioClient {
    const headers = { Authorization: 'Basic ' + btoa(opts.accountSid + ':' + opts.authToken) };
    const retryOpts = {
        retries: 2,
        exponentialBackoff: opts.retryOpts ? opts.retryOpts.exponentialBackOff : 500,
    };

    return {
        sanitizePhoneNumber: async (phoneNumber) => {
            // Retry on request errors and 429/5xx responses:
            const response = await retry(
                () =>
                    fetch(`https://lookups.twilio.com/v1/PhoneNumbers/${encodeURIComponent(phoneNumber)}`, {
                        headers,
                    }).then(async (response) => {
                        if (response.status === 429 || response.status >= 500) {
                            throw await ResponseError.fromResponse('Twilio phone number lookup failed', response);
                        }

                        return response;
                    }),
                retryOpts,
            );

            if (response.status === 404) {
                return null;
            }

            if (!response.ok) {
                throw await ResponseError.fromResponse('Twilio phone number lookup failed', response);
            }

            const body = await response.json();
            const isValid =
                body &&
                typeof body === 'object' &&
                body.phone_number &&
                typeof body.phone_number === 'string' &&
                body.national_format &&
                typeof body.national_format === 'string';

            if (!isValid) {
                throw Error('Invalid response body: ' + JSON.stringify(body));
            }

            return { sanitizedPhoneNumber: body.phone_number, localePhoneNumber: body.national_format };
        },

        sendSms: async (messageOpts) => {
            const body = new URLSearchParams({
                Body: messageOpts.body,
                From: opts.fromNumber,
                To: messageOpts.receiver,
            });

            // Retry on request errors and 429/5xx responses:
            const response = await retry(
                () =>
                    fetch(`https://api.twilio.com/2010-04-01/Accounts/${opts.accountSid}/Messages.json`, {
                        method: 'POST',
                        headers,
                        body,
                    }).then(async (response) => {
                        if (response.status === 429 || response.status >= 500) {
                            throw await ResponseError.fromResponse('Twilio message send failed', response);
                        }

                        return response;
                    }),
                retryOpts,
            );

            if (!response.ok) {
                throw await ResponseError.fromResponse('Twilio message send failed', response);
            }
        },
    };
}

export function createStubTwilioClient(errorMessage: string): TwilioClient {
    return {
        sanitizePhoneNumber: () => Promise.reject(errorMessage),
        sendSms: () => Promise.reject(errorMessage),
    };
}
